import { useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconArrow, IconMenu } from '@components/icons';
import { NightgraphLogo } from '@components/icons/src/nightgraph-logo';
import { MenuButtonActions } from '@components/menu-button-actions';
import { NavigationBackButton } from '@components/navigation-back-button';
import {
  ENABLED_BOX_OFFICE_BUTTON,
  ENABLED_CREATE_EVENT_BUTTON,
  ENABLED_INVITATIONS_BUTTON,
  ENABLED_OFFLINE_SALES_BUTTON,
} from '@constants/company-auth-permissions';
import { EVENT_CREATE_INFO_ROUTE, EVENT_LIST } from '@constants/routes';
import { LG_VIEWPORT, XS_VIEWPORT } from '@constants/viewport';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { NavigationContext } from '@contexts/navigation/navigation-context';
import { useCurrentEvent } from '@hooks/events/use-current-event';
import { VIEW_UPCOMING } from '@hooks/use-event-navigation/use-event-navigation';
import { useIsProtectedElement } from '@hooks/use-is-protected-element';
import { useWindowSize } from '@hooks/use-window-size';
import { getFilteredModalActions } from '@utils/get-filtered-modal-actions';
import classNames from 'classnames';

import { MenuVenue } from '../menu-venue/menu-venue';

import { Props } from './menu-top.types';

export const MenuTop = ({ handleIsMenuOpenInMobile }: Props) => {
  const viewportLessThanDesktop = useWindowSize(LG_VIEWPORT as number);
  const viewportLessThanMobile = useWindowSize(XS_VIEWPORT);

  const { event } = useCurrentEvent();
  const { clubState, companyState } = useContext(CompanyClubContext);
  const navigate = useNavigate();
  const { preventRedirect, setIsItemClicked, setRedirectUrl } = useContext(NavigationContext);
  const isEventCreationEnabled = useIsProtectedElement({ permissions: ENABLED_CREATE_EVENT_BUTTON });
  const isInvitationsEnabled = useIsProtectedElement({ permissions: ENABLED_INVITATIONS_BUTTON });
  const isOfflineSalesEnabled = useIsProtectedElement({ permissions: ENABLED_OFFLINE_SALES_BUTTON });
  const isBoxOfficeEnabled = useIsProtectedElement({ permissions: ENABLED_BOX_OFFICE_BUTTON });
  const location = useLocation();

  const inOffersOrInsights = location.pathname.includes('/offers/list') || location.pathname.includes('/insights');

  const showBackButton = viewportLessThanMobile && (event?.name || inOffersOrInsights);
  const showMenuButton = viewportLessThanDesktop && !(viewportLessThanMobile && inOffersOrInsights);
  const showVenueMenu = !viewportLessThanMobile || !inOffersOrInsights;
  const onUpcoming = location.pathname.includes(VIEW_UPCOMING);
  const navigateToUpcoming = () => {
    if (onUpcoming) return;
    if (preventRedirect) {
      setIsItemClicked(true);
      setRedirectUrl(
        EVENT_LIST.replace(':companyId', companyState.legacyId)
          .replace(':clubSlug', clubState.slug)
          .replace(':view', VIEW_UPCOMING)
      );
    } else {
      navigate(
        EVENT_LIST.replace(':companyId', companyState.legacyId)
          .replace(':clubSlug', clubState.slug)
          .replace(':view', VIEW_UPCOMING)
      );
    }
  };
  const navigateToCreateEvent = () => {
    const eventCreateRoute = EVENT_CREATE_INFO_ROUTE.replace(':companyId', companyState.legacyId).replace(
      ':clubSlug',
      clubState.slug
    );
    if (preventRedirect) {
      setIsItemClicked(true);
      setRedirectUrl(eventCreateRoute);
    } else {
      navigate(eventCreateRoute);
    }
  };
  const defaultBackRoute = EVENT_LIST.replace(':companyId', companyState.legacyId)
    .replace(':clubSlug', clubState.slug)
    .replace(':view', VIEW_UPCOMING);

  const filteredActions = useMemo(
    () =>
      getFilteredModalActions({
        offlineSale: isOfflineSalesEnabled,
        inviteGuest: isInvitationsEnabled,
        boxOffice: isBoxOfficeEnabled,
      }),
    [isOfflineSalesEnabled, isInvitationsEnabled, isBoxOfficeEnabled]
  );

  return (
    <header
      className={classNames(
        'sticky top-0 z-20 flex flex-row items-center justify-between bg-secondary py-3 md:py-4 px-4 md:px-6 lg:px-10 lg:relative',
        { 'pt-10': !showMenuButton }
      )}>
      <div className='flex flex-row items-center'>
        {showBackButton && (
          <NavigationBackButton defaultRoute={defaultBackRoute} forceDefaultRoute={inOffersOrInsights}>
            <IconArrow className='w-5 h-5 text-primary rotate-180' />
          </NavigationBackButton>
        )}
        {showMenuButton && (
          <button type='button' aria-label='Menu' onClick={handleIsMenuOpenInMobile}>
            <IconMenu className='w-5 h-5 text-primary' />
          </button>
        )}
        <button
          className={classNames('hidden lg:block text-primary', { 'pointer-events-none': onUpcoming })}
          onClick={navigateToUpcoming}>
          <NightgraphLogo className='w-[180px] text-primary' />
        </button>
      </div>

      {showVenueMenu && !event?.name && !!clubState.slug && (
        <div className='relative flex items-start gap-6'>
          <div className='relative'>
            <MenuVenue />
          </div>
          {!viewportLessThanMobile && Object.entries(filteredActions).length > 0 && (
            <MenuButtonActions
              label='common:addNew'
              navigateToCreateEvent={isEventCreationEnabled ? navigateToCreateEvent : undefined}
              actions={filteredActions}
            />
          )}
        </div>
      )}
    </header>
  );
};
